import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import Fleet from './components/Fleet';
import Coverage from './components/Coverage';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Recruiting from './components/Recruiting';
import Navigation from './components/Navigation';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaData = [
  {
    '@context': 'https://schema.org',
    '@type': 'TruckingService',
    name: 'Monarch Xpress LLC',
    description:
      'Dallas, TX trucking company specializing in dry van and flatbed freight across all 48 states. Services include cross dock, warehousing, load restacking, final mile delivery, drop trailer, and storage trailer.',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    telephone: '+1-214-444-6777',
    email: 'dispatch@monarchxp.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dallas',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 32.7767,
      longitude: -96.797,
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    serviceType: [
      'Dry Van Freight',
      'Flatbed Hauling',
      'Cross Dock Services',
      'Warehousing',
      'Load Restacking',
      'Final Mile Delivery',
      'Drop Trailer',
      'Storage Trailer',
      'Regional Freight',
      'Over the Road (OTR)',
    ],
    sameAs: [],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: 'Monarch Xpress LLC',
    description:
      'Professional trucking and freight services based in Dallas, TX serving all 48 states.',
    url: SITE_URL,
    telephone: '+1-214-444-6777',
    email: 'dispatch@monarchxp.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Dallas',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 32.7767,
      longitude: -96.797,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '00:00',
        closes: '23:59',
      },
    ],
    priceRange: '$$',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What states does Monarch Xpress LLC serve?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Monarch Xpress LLC operates across all 48 contiguous states, providing both regional and over-the-road (OTR) freight services from our Dallas, TX hub.',
        },
      },
      {
        '@type': 'Question',
        name: 'What types of freight does Monarch Xpress LLC haul?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We specialize in dry van and flatbed freight. Additional services include cross dock, warehousing, load restacking, final mile delivery, drop trailer, and storage trailer solutions.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does Monarch Xpress LLC offer warehousing and cross dock services?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. We offer full warehousing, cross dock, and load restacking services from our Dallas, TX facility to support your supply chain needs.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can I contact Monarch Xpress LLC?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can reach us by phone at 214-444-6777 or by email at dispatch@monarchxp.com. Our dispatch team is available 24/7.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Monarch Xpress LLC hiring truck drivers?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! We are actively recruiting experienced truck drivers. Apply online at our driver application portal for competitive pay, benefits, and modern equipment.',
        },
      },
    ],
  },
];

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[data-schema="monarch-xpress"]');
    existingScripts.forEach((s) => s.remove());

    schemaData.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'monarch-xpress');
      script.setAttribute('data-schema-index', String(index));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="monarch-xpress"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      <Hero />
      <Services />
      <Fleet />
      <Coverage />
      <Testimonials />
      <Contact />
      <Recruiting />
      <Footer />
    </div>
  );
}
